import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import {
  $Button,
  $Popconfirm,
  $Skeleton,
  $Input,
  $Tabs,
  $Tooltip,
} from "us.common/components";
import { Formik } from "formik";
import _ from "lodash";
import * as Actions from "actions";
import {
  getInitialValues,
  getModifiedListForUpdate,
  getSettingModifiedList,
  getSettingTypeList,
  getSettingsByType,
  setPasswordVisibility,
} from "./Functions";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "us.icons";
import "./Settings.scss";
import { createValidationSchema } from "./Validations";

interface ISettings {
  onClose: () => void;
}
/**
 * @description -USC settings drawer Content View
 * @link Design Document - https://unicorn-solutions.atlassian.net/l/cp/vykMiuKf
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 01/10/2023
 */

const Settings: React.FC<PropsFromRedux & ISettings> = (props) => {
  const { t } = useTranslation();
  const { settings, updateSettings, onClose } = props;

  const [settingsDataList, setSettingsDataList] = useState<any>([]);
  const [selectedSettingId, setSelectedSettingId] = useState<number>(0);

  useEffect(() => {
    if (settings?.data) {
      setSettingsDataList(getSettingModifiedList(settings?.data));
    }
  }, [settings?.data]);

  const settingList = useMemo(() => {
    return getInitialValues(settingsDataList);
  }, [settingsDataList]);

  const settingTypeList = useMemo(() => {
    return getSettingTypeList(settingsDataList);
  }, [settingsDataList]);

  const validationSchema = createValidationSchema(settingList);

  /**
   * @function
   * @description update settings
   */
  const updateUSCSettings = (values: any) => {
    const changedValues = getModifiedListForUpdate(settingsDataList, values);
    updateSettings && updateSettings(changedValues);
    onClose && onClose();
  };

  return (
    <Formik
      initialValues={settingList}
      enableReinitialize
      onSubmit={updateUSCSettings}
      validationSchema={validationSchema}
    >
      {({
        values,
        setFieldValue,
        initialValues,
        handleSubmit,
        ...restProps
      }: any) => (
        <div className="usc-settings">
          <$Skeleton loading={settings?.isLoading}>
          
          {/* commented due to [antd: Tabs] Tabs.TabPane is deprecated */}

            {/* <$Tabs tabPosition="left" className="usc-settings-tabs">
              {settingTypeList.map((type: string) => {
                return (
                  <$Tabs.TabPane className="" tab={type} key={type}>
                    <div className="pt-1 pl-3">
                      <h2 className="sub-title mb-3" data-test-id="type">
                        {type}
                      </h2>

                      {getSettingsByType(settingsDataList, type).map(
                        (setting) => {
                          const {
                            settingName: fieldName,
                            settingId,
                            isPassword,
                            showPassword,
                          } = setting;
                          return (
                            <div className="settings-item-wrap" key={settingId}>
                              {(selectedSettingId == 0 ||
                                !selectedSettingId ||
                                selectedSettingId != settingId) && (
                                <div
                                  className={`settings-item ${
                                    restProps.touched[fieldName] &&
                                    values[fieldName] !=
                                      initialValues[fieldName]
                                      ? "settings-item-updated"
                                      : ""
                                  }`}
                                >
                                  {isPassword && (
                                    <$Input
                                      size="small"
                                      name={fieldName}
                                      maxLength={600}
                                      autoComplete="off"
                                      addonBefore={
                                        <$Tooltip
                                          placement="top"
                                          title={fieldName}
                                        >
                                          {fieldName}
                                        </$Tooltip>
                                      }
                                      prefix={
                                        <>
                                          {!showPassword
                                            ? values[fieldName] &&
                                              Array.from(values[fieldName]).map(
                                                (_, index) => (
                                                  <span key={index}>●</span>
                                                )
                                              )
                                            : values[fieldName]}
                                        </>
                                      }
                                      suffix={
                                        <$Tooltip
                                          placement="top"
                                          title={t(
                                            "US.COLLECTION.COMMON:COMMON.SHOW_PASSWORD"
                                          )}
                                        >
                                          <$Button
                                            size="small"
                                            type="link"
                                            onClick={() =>
                                              setSettingsDataList(
                                                setPasswordVisibility(
                                                  settingsDataList,
                                                  fieldName
                                                )
                                              )
                                            }
                                          >
                                            {showPassword && (
                                              <EyeInvisibleOutlined />
                                            )}

                                            {!showPassword && <EyeOutlined />}
                                          </$Button>
                                        </$Tooltip>
                                      }
                                      addonAfter={
                                        <>
                                          <$Tooltip
                                            placement="top"
                                            title={t(
                                              "US.COLLECTION.COMMON:COMMON.EDIT"
                                            )}
                                          >
                                            <$Button
                                              size="small"
                                              type="link"
                                              onClick={() =>
                                                setSelectedSettingId(settingId)
                                              }
                                              data-test-id="edit-btn"
                                              className="mx-1"
                                            >
                                              <EditOutlined />
                                            </$Button>
                                          </$Tooltip>
                                        </>
                                      }
                                    />
                                  )}
                                  {!isPassword && (
                                    <$Input
                                      size="small"
                                      name={fieldName}
                                      maxLength={600}
                                      autoComplete="off"
                                      addonBefore={
                                        <$Tooltip
                                          placement="top"
                                          title={fieldName}
                                        >
                                          {fieldName}
                                        </$Tooltip>
                                      }
                                      prefix={<>{values[fieldName]}</>}
                                      suffix={
                                        <$Tooltip
                                          placement="top"
                                          title={t(
                                            "US.COLLECTION.COMMON:COMMON.SHOW_PASSWORD"
                                          )}
                                        ></$Tooltip>
                                      }
                                      addonAfter={
                                        <>
                                          <$Tooltip
                                            placement="top"
                                            title={t(
                                              "US.COLLECTION.COMMON:COMMON.EDIT"
                                            )}
                                          >
                                            <$Button
                                              size="small"
                                              type="link"
                                              onClick={() =>
                                                setSelectedSettingId(settingId)
                                              }
                                              data-test-id="edit-btn"
                                              className="mx-1"
                                            >
                                              <EditOutlined />
                                            </$Button>
                                          </$Tooltip>
                                        </>
                                      }
                                    />
                                  )}
                                </div>
                              )}
                              {selectedSettingId == settingId && (
                                <div
                                  className={`settings-item settings-item-edit ${
                                    restProps.touched[fieldName] &&
                                    values[fieldName] !=
                                      initialValues[fieldName]
                                      ? "settings-item-updated"
                                      : ""
                                  }`}
                                >
                                  <$Input
                                    size="small"
                                    name={fieldName}
                                    maxLength={600}
                                    autoComplete="off"
                                    addonBefore={
                                      <$Tooltip
                                        placement="top"
                                        title={fieldName}
                                      >
                                        {fieldName}
                                      </$Tooltip>
                                    }
                                    addonAfter={
                                      <>
                                        <$Tooltip
                                          placement="top"
                                          title={t(
                                            "US.COLLECTION.COMMON:COMMON.SAVE"
                                          )}
                                        >
                                          <$Button
                                            size="small"
                                            type="primary"
                                            onClick={() => {
                                              setSelectedSettingId(0);
                                            }}
                                            data-test-id="save-btn"
                                            className="mx-1"
                                          >
                                            <CheckOutlined />
                                          </$Button>
                                        </$Tooltip>
                                        <$Tooltip
                                          placement="top"
                                          title={t(
                                            "US.COLLECTION.COMMON:COMMON.CANCEL"
                                          )}
                                        >
                                          <$Button
                                            type="link"
                                            size="small"
                                            onClick={() => {
                                              setFieldValue(
                                                fieldName,
                                                initialValues[fieldName]
                                              );
                                              setSelectedSettingId(0);
                                            }}
                                            className="mx-1"
                                          >
                                            <CloseOutlined />
                                          </$Button>
                                        </$Tooltip>
                                      </>
                                    }
                                  />
                                </div>
                              )}

                              {restProps.touched[fieldName] &&
                                values[fieldName] != initialValues[fieldName] &&
                                !restProps.errors[fieldName] && (
                                  <div>
                                    <span
                                      data-test-id="change-description"
                                      className="ant-form-item-explain"
                                    >
                                      {t(
                                        "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.THIS_VALUE_HAS_BEEN_CHANGED"
                                      )}
                                    </span>
                                  </div>
                                )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </$Tabs.TabPane>
                );
              })}
            </$Tabs> */}
            <$Tabs
              tabPosition="left"
              className="usc-settings-tabs"
              items={settingTypeList.map((type: string) => ({
                label: type,
                key: type,
                children: (
                  <div className="pt-1 pl-3">
                    <h2 className="sub-title mb-3" data-test-id="type">
                      {type}
                    </h2>
                    {getSettingsByType(settingsDataList, type).map((setting) => {
                      const {
                        settingName: fieldName,
                        settingId,
                        isPassword,
                        showPassword,
                      } = setting;
                      return (
                        <div className="settings-item-wrap" key={settingId}>
                          {(selectedSettingId == 0 ||
                            !selectedSettingId ||
                            selectedSettingId != settingId) && (
                              <div
                                className={`settings-item ${restProps.touched[fieldName] &&
                                    values[fieldName] != initialValues[fieldName]
                                    ? "settings-item-updated"
                                    : ""
                                  }`}
                              >
                                {isPassword && (
                                  <$Input
                                    size="small"
                                    name={fieldName}
                                    maxLength={600}
                                    autoComplete="off"
                                    addonBefore={
                                      <$Tooltip placement="top" title={fieldName}>
                                        {fieldName}
                                      </$Tooltip>
                                    }
                                    prefix={
                                      <>
                                        {!showPassword
                                          ? values[fieldName] &&
                                          Array.from(values[fieldName]).map((_, index) => (
                                            <span key={index}>●</span>
                                          ))
                                          : values[fieldName]}
                                      </>
                                    }
                                    suffix={
                                      <$Tooltip
                                        placement="top"
                                        title={t(
                                          "US.COLLECTION.COMMON:COMMON.SHOW_PASSWORD"
                                        )}
                                      >
                                        <$Button
                                          size="small"
                                          type="link"
                                          onClick={() =>
                                            setSettingsDataList(
                                              setPasswordVisibility(
                                                settingsDataList,
                                                fieldName
                                              )
                                            )
                                          }
                                        >
                                          {showPassword && <EyeInvisibleOutlined />}
                                          {!showPassword && <EyeOutlined />}
                                        </$Button>
                                      </$Tooltip>
                                    }
                                    addonAfter={
                                      <$Tooltip
                                        placement="top"
                                        title={t("US.COLLECTION.COMMON:COMMON.EDIT")}
                                      >
                                        <$Button
                                          size="small"
                                          type="link"
                                          onClick={() => setSelectedSettingId(settingId)}
                                          data-test-id="edit-btn"
                                          className="mx-1"
                                        >
                                          <EditOutlined />
                                        </$Button>
                                      </$Tooltip>
                                    }
                                  />
                                )}
                                {!isPassword && (
                                  <$Input
                                    size="small"
                                    name={fieldName}
                                    maxLength={600}
                                    autoComplete="off"
                                    addonBefore={
                                      <$Tooltip placement="top" title={fieldName}>
                                        {fieldName}
                                      </$Tooltip>
                                    }
                                    prefix={<>{values[fieldName]}</>}
                                    addonAfter={
                                      <$Tooltip
                                        placement="top"
                                        title={t("US.COLLECTION.COMMON:COMMON.EDIT")}
                                      >
                                        <$Button
                                          size="small"
                                          type="link"
                                          onClick={() => setSelectedSettingId(settingId)}
                                          data-test-id="edit-btn"
                                          className="mx-1"
                                        >
                                          <EditOutlined />
                                        </$Button>
                                      </$Tooltip>
                                    }
                                  />
                                )}
                              </div>
                            )}
                          {selectedSettingId == settingId && (
                            <div
                              className={`settings-item settings-item-edit ${restProps.touched[fieldName] &&
                                  values[fieldName] != initialValues[fieldName]
                                  ? "settings-item-updated"
                                  : ""
                                }`}
                            >
                              <$Input
                                size="small"
                                name={fieldName}
                                maxLength={600}
                                autoComplete="off"
                                addonBefore={
                                  <$Tooltip placement="top" title={fieldName}>
                                    {fieldName}
                                  </$Tooltip>
                                }
                                addonAfter={
                                  <>
                                    <$Tooltip
                                      placement="top"
                                      title={t("US.COLLECTION.COMMON:COMMON.SAVE")}
                                    >
                                      <$Button
                                        size="small"
                                        type="primary"
                                        onClick={() => setSelectedSettingId(0)}
                                        data-test-id="save-btn"
                                        className="mx-1"
                                      >
                                        <CheckOutlined />
                                      </$Button>
                                    </$Tooltip>
                                    <$Tooltip
                                      placement="top"
                                      title={t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                                    >
                                      <$Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                          setFieldValue(
                                            fieldName,
                                            initialValues[fieldName]
                                          );
                                          setSelectedSettingId(0);
                                        }}
                                        className="mx-1"
                                      >
                                        <CloseOutlined />
                                      </$Button>
                                    </$Tooltip>
                                  </>
                                }
                              />
                            </div>
                          )}
                          {restProps.touched[fieldName] &&
                            values[fieldName] != initialValues[fieldName] &&
                            !restProps.errors[fieldName] && (
                              <div>
                                <span
                                  data-test-id="change-description"
                                  className="ant-form-item-explain"
                                >
                                  {t(
                                    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.THIS_VALUE_HAS_BEEN_CHANGED"
                                  )}
                                </span>
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                ),
              }))}
            />
          </$Skeleton>

          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Button
                className="mr-2"
                disabled={!restProps.dirty || !restProps.isValid}
                onClick={handleSubmit}
                type="primary"
                data-test-id="update-btn"
              >
                {t("COMMON.UPDATE")}
              </$Button>
              {restProps.dirty && (
                <$Popconfirm
                  title={t(
                    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DO_YOU_WANT_TO_DISCARD_THE_CHANGES?"
                  )}
                  placement="topLeft"
                  onConfirm={() => onClose()}
                  okText="Yes"
                  cancelText="No"
                >
                  <$Button>{t("COMMON.CANCEL")}</$Button>
                </$Popconfirm>
              )}
              {!restProps.dirty && (
                <$Button onClick={() => onClose()}>
                  {t("COMMON.CANCEL")}
                </$Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: any) => {
  const { editorHome } = state;
  const { settings } = editorHome;
  return {
    settings,
  };
};

const { settings } = Actions.editorHome;

const mapDispatchToProps = {
  updateSettings: settings.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(Settings);
